import { RotagenApiError } from '@/api/rotagen-api';
import { translateErrorCode } from '@/lib/error-utils';

/**
 * Translated strings supplied by the caller — resolved by the Lingui macro
 * inside a component scope, then passed in as plain text so this helper stays
 * macro-free (the macro can't follow a `t` binding through a parameter).
 */
export interface FormatCommitErrorMessages {
  consentHint: string;
  fallbackMessage: string;
}

/**
 * Turns the opaque commit error into user-visible copy. Routes
 * `RotagenApiError` codes through `translateErrorCode`; for the
 * conflict-without-consent case, appends the caller-supplied
 * `messages.consentHint` pointing at the replace-existing checkbox.
 */
export function formatCommitError(
  error: unknown,
  replaceExistingRota: boolean,
  messages: FormatCommitErrorMessages,
): string {
  if (error instanceof RotagenApiError) {
    // Prefer the API's per-issue messages — they say exactly what's wrong
    // (e.g. "Existing rota 750 has draft shifts") — over the generic top-level
    // message. Fall back to the translated code when no issues are attached.
    // Dedupe identical messages — a rejection often repeats the same message
    // once per offending shift, which reads as noise.
    const detail =
      error.issues.length > 0
        ? [...new Set(error.issues.map((issue) => issue.message))].join('\n')
        : translateErrorCode(error.code, error.message);
    // The "existing draft shifts" rejection has a dedicated checkbox; point the
    // user at it.
    if (!replaceExistingRota && isExistingShiftsConflict(error)) {
      return `${detail}\n${messages.consentHint}`;
    }
    return detail;
  }
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return messages.fallbackMessage;
}

/** Issue code rotagen attaches when a commit would overwrite existing drafts. */
const EXISTING_ROTA_REPLACE_REQUIRES_CONSENT = 'EXISTING_ROTA_REPLACE_REQUIRES_CONSENT';

/**
 * Detects the rotagen "existing draft shifts" rejection. Historically this
 * surfaced as a 409 `ConflictError`; the commit-validation phase now reports it
 * as a 400 validation error carrying an `EXISTING_ROTA_REPLACE_REQUIRES_CONSENT`
 * issue, so match either form.
 */
function isExistingShiftsConflict(error: RotagenApiError): boolean {
  return (
    error.statusCode === 409 ||
    error.category === 'conflict' ||
    error.issues.some((issue) => issue.code === EXISTING_ROTA_REPLACE_REQUIRES_CONSENT)
  );
}
